import React, { useState, useEffect } from 'react';
import { Phone, PhoneOutgoing, PhoneIncoming, CheckCircle2, AlertCircle, RefreshCw, Plus, Trash2, Globe, PhoneCall, ShieldCheck, Loader2, Languages } from 'lucide-react';
import toast from 'react-hot-toast';
import api, { organizationsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useBrowserCall } from '../contexts/BrowserCallContext';

// Settings UI for the per-org VoIP integration. Supports NetSapiens and
// VoIP.ms. The active provider is set via a dropdown; the form below
// swaps in place to expose only the relevant credential fields. Both
// providers' credentials persist across switches (server side stores
// them on `Organization.voip.*`) so an operator can move between
// providers without re-entering setup.
// Languages callers speak, for call-recording transcripts. Mirrors
// CALL_LANGUAGES in server/services/aiTranscript.js.
const CALL_LANGUAGE_OPTIONS = [
  ['en', 'English'], ['es', 'Spanish'], ['fa', 'Persian (Farsi)'], ['hy', 'Armenian'], ['ru', 'Russian'],
  ['ko', 'Korean'], ['zh', 'Chinese'], ['vi', 'Vietnamese'], ['tl', 'Tagalog'], ['ar', 'Arabic'],
  ['he', 'Hebrew'], ['tr', 'Turkish'], ['fr', 'French'], ['de', 'German'], ['pt', 'Portuguese'],
  ['it', 'Italian'], ['ja', 'Japanese'], ['hi', 'Hindi'],
];

const PROVIDERS = [
  { value: 'netsapiens', label: 'NetSapiens (Dash Manager)' },
  // White-labelled: clients see our reseller brand, not the upstream
  // provider name. The internal value stays 'voipms'.
  { value: 'voipms', label: 'Gain Rhino VoIP' },
];

// Full IANA list when the browser exposes it; a pragmatic shortlist
// otherwise (Safari < 15.4 and friends).
const TZ_OPTIONS = typeof Intl.supportedValuesOf === 'function'
  ? Intl.supportedValuesOf('timeZone')
  : ['UTC', 'America/Los_Angeles', 'America/Denver', 'America/Chicago', 'America/New_York', 'Europe/London', 'Europe/Istanbul'];

const EMPTY_DRAFT = {
  // NetSapiens
  baseUrl: '', clientId: '', clientSecret: '', username: '', password: '', domain: '',
  // VoIP.ms (reseller model): master api credentials are server-side env;
  // the client manages a list of lines, one per sub-account.
  // Each: { subaccount, did, user }.
  subaccounts: [],
};

// Build the editable draft from a /voip/settings response. VoIP.ms lines
// come from the new `subaccounts` array; an org configured before
// multi-line support has only the legacy single subaccount/smsDid, which
// we surface as one starter row so the admin sees their existing setup.
function draftFromData(data) {
  let lines = Array.isArray(data.subaccounts) ? data.subaccounts.map((l) => ({
    subaccount: l.subaccount || '', did: l.did || '', user: l.user || '', label: l.label || '',
  })) : [];
  if (lines.length === 0 && data.subaccount) {
    lines = [{ subaccount: data.subaccount, did: data.smsDid || '', user: '' }];
  }
  return {
    provider: data.provider || 'netsapiens',
    baseUrl: data.baseUrl || '',
    clientId: '',
    clientSecret: '',
    username: data.username || '',
    password: '',
    domain: data.domain || '',
    subaccounts: lines,
  };
}

const VoipSettings = () => {
  const { user: currentUser, refreshUser } = useAuth();
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  // Org users for the per-line rep dropdown (call attribution).
  const [orgUsers, setOrgUsers] = useState([]);
  // Self-extension card lives on this page too because superadmins are
  // hidden from Team Members and would otherwise have no UI path to
  // assign themselves an extension / sub-account.
  const [myExt, setMyExt] = useState('');
  const [savingExt, setSavingExt] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  // Live "calls made today" counts straight from the provider. null =
  // not loaded (unconfigured org or fetch failed) — badges simply hide.
  const [callCounts, setCallCounts] = useState(null);
  const [countsLoading, setCountsLoading] = useState(false);
  // Browser calling (click-to-call): monthly quota + per-line caller-ID
  // verification state. `verify` tracks one in-flight Twilio Verified
  // Caller ID flow: { idx, phoneNumber, code, status, error }.
  const [bcUsage, setBcUsage] = useState(null);
  const [verify, setVerify] = useState(null);
  const { refreshEligibility } = useBrowserCall();
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  useEffect(() => { load(); }, []);
  useEffect(() => {
    setMyExt(currentUser?.voip?.extension || '');
  }, [currentUser?._id, currentUser?.voip?.extension]);

  const provider = draft.provider || settings?.provider || 'netsapiens';

  const saveMyExt = async () => {
    if (!currentUser?._id) return;
    setSavingExt(true);
    try {
      await api.put(`/voip/user-extension/${currentUser._id}`, { extension: myExt.trim() });
      toast.success(myExt.trim() ? `Your ${provider === 'voipms' ? 'sub-account' : 'extension'} is now ${myExt.trim()}` : 'Extension cleared');
      if (typeof refreshUser === 'function') await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save extension');
    } finally {
      setSavingExt(false);
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get('/voip/settings');
      setSettings(data);
      // Pre-populate the form with non-secret values so a returning admin
      // sees what's configured. Secret fields stay blank — the server
      // returns them masked which would either round-trip the mask back
      // or expose them in the DOM. Hint text says "leave blank to keep".
      setDraft(draftFromData(data));
    } catch (err) {
      setMessage(err.response?.data?.error || 'Failed to load VoIP settings');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
    // Rep dropdown source — non-blocking; failure just leaves it empty.
    try {
      const { data } = await api.get('/organizations/users');
      setOrgUsers(Array.isArray(data?.users) ? data.users : []);
    } catch (_) { /* dropdown stays empty */ }
    loadCallCounts();
    loadBcUsage();
  };

  const loadBcUsage = async () => {
    try {
      const { data } = await api.get('/browser-call/usage');
      setBcUsage(data);
    } catch (_) {
      setBcUsage(null);
    }
  };

  // Re-read the saved settings without touching the draft (used after a
  // caller-ID verification lands so unsaved edits survive).
  const refreshSettingsOnly = async () => {
    try {
      const { data } = await api.get('/voip/settings');
      setSettings(data);
    } catch (_) { /* keep what we have */ }
  };

  // A line's saved (server-side) record. Verification is server-owned
  // state, so the badge reads from `settings`, never from the draft.
  const savedLineFor = (line) => (settings?.subaccounts || []).find((l) => l.subaccount === line.subaccount) || null;
  const lineDigits = (d) => String(d || '').replace(/\D/g, '');
  const lineVerified = (line) => {
    const saved = savedLineFor(line);
    return !!(saved && saved.callerIdVerified && lineDigits(saved.did) === lineDigits(line.did) && lineDigits(line.did));
  };
  const lineNeedsSave = (line) => {
    const saved = savedLineFor(line);
    return !saved || lineDigits(saved.did) !== lineDigits(line.did);
  };

  const startVerify = async (line) => {
    const savedIdx = (settings?.subaccounts || []).findIndex((l) => l.subaccount === line.subaccount);
    if (savedIdx < 0) return;
    setVerify({ idx: savedIdx, subaccount: line.subaccount, status: 'starting', code: '', phoneNumber: '', error: '' });
    try {
      const { data } = await api.post('/browser-call/verify-caller-id', { lineIdx: savedIdx });
      if (data.verified) {
        setVerify(null);
        toast.success(`${data.phoneNumber} is verified for browser calling`);
        await refreshSettingsOnly();
        refreshEligibility();
        return;
      }
      setVerify({ idx: savedIdx, subaccount: line.subaccount, status: 'waiting', code: data.validationCode, phoneNumber: data.phoneNumber, error: '' });
    } catch (err) {
      setVerify({ idx: savedIdx, subaccount: line.subaccount, status: 'error', code: '', phoneNumber: '', error: err.response?.data?.error || 'Could not start verification' });
    }
  };

  // Poll Twilio (via our API) every 3s while a verification is waiting;
  // give up after 5 minutes (Twilio's own code window is ~10 min).
  useEffect(() => {
    if (!verify || verify.status !== 'waiting') return undefined;
    const startedAt = Date.now();
    const timer = setInterval(async () => {
      if (Date.now() - startedAt > 5 * 60_000) {
        setVerify((v) => (v ? { ...v, status: 'error', error: 'Timed out waiting for the code. Click Verify to try again.' } : v));
        return;
      }
      try {
        const { data } = await api.get(`/browser-call/verify-caller-id/${verify.idx}`);
        if (data.verified) {
          clearInterval(timer);
          setVerify(null);
          toast.success(`${data.phoneNumber} is verified for browser calling`);
          await refreshSettingsOnly();
          refreshEligibility();
        }
      } catch (_) { /* transient; keep polling */ }
    }, 3000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verify?.status, verify?.idx]);

  // Non-blocking: the badge is informational, a provider hiccup must not
  // break the settings form.
  const loadCallCounts = async () => {
    setCountsLoading(true);
    try {
      const { data } = await api.get('/voip/call-counts-today');
      setCallCounts(data?.total ? data : null);
    } catch (_) {
      setCallCounts(null);
    } finally {
      setCountsLoading(false);
    }
  };

  // Percent formatter: 0.6667 → "67%". Null-safe ("—" when no data).
  const pct = (v) => (v == null ? '—' : `${Math.round(v * 100)}%`);

  // Org timezone — drives where the "today" window of the call counters
  // rolls over (server computes org-local midnight from this).
  const [orgTz, setOrgTz] = useState('UTC');
  const [savingTz, setSavingTz] = useState(false);
  const isOrgAdmin = currentUser?.role === 'admin';
  // Gain Rhino VoIP sub-accounts and numbers are assigned by Gain Rhino
  // (the server rejects tenant edits): admins only pick the rep per line.
  const canAssignLines = currentUser?.role === 'superadmin';
  useEffect(() => {
    organizationsAPI.getInfo()
      .then(({ data }) => setOrgTz(data?.organization?.settings?.timezone || 'UTC'))
      .catch(() => {});
  }, []);

  // Saved on click, like the timezone. Never empty: the last one stays.
  const [savingLangs, setSavingLangs] = useState(false);
  const toggleCallLanguage = async (code) => {
    const current = settings?.callLanguages?.length ? settings.callLanguages : ['en'];
    const next = current.includes(code) ? current.filter((c) => c !== code) : [...current, code];
    if (!next.length) { toast.error('Keep at least one language'); return; }
    setSavingLangs(true);
    try {
      const { data } = await api.put('/voip/settings', { callLanguages: next });
      setSettings((s) => ({ ...s, callLanguages: data.callLanguages }));
      toast.success('Call languages updated');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not update call languages');
    } finally {
      setSavingLangs(false);
    }
  };

  const onTimezoneChange = async (e) => {
    const next = e.target.value;
    const prev = orgTz;
    setOrgTz(next);
    setSavingTz(true);
    try {
      await organizationsAPI.updateSettings({ timezone: next });
      toast.success('Organization timezone updated');
      // Server caches call counts for 60s per org, so the badges may lag
      // one refresh cycle behind the new day window.
      loadCallCounts();
    } catch (err) {
      setOrgTz(prev);
      toast.error(err.response?.data?.message || 'Could not update timezone');
    } finally {
      setSavingTz(false);
    }
  };

  // Shared-DID inbound bucket for a line (2+ sub-accounts on one number:
  // provider CDRs can't say which line an inbound call belongs to, so the
  // server reports those per-DID and the UI renders them as "shared").
  const sharedForLine = (line) => {
    if (!callCounts?.sharedDids?.length) return null;
    const digits = String(line.did || '').replace(/\D/g, '').slice(-10);
    if (!digits) return null;
    return callCounts.sharedDids.find(
      (s) => String(s.did || '').replace(/\D/g, '').slice(-10) === digits
    ) || null;
  };

  // Look up today's counts for an editable line row (matched by
  // sub-account first, then by DID digits).
  const countsForLine = (line) => {
    if (!callCounts?.lines?.length) return null;
    const digits = String(line.did || '').replace(/\D/g, '').slice(-10);
    return (
      callCounts.lines.find((c) => c.subaccount && c.subaccount === line.subaccount) ||
      (digits ? callCounts.lines.find((c) => String(c.did || '').replace(/\D/g, '').slice(-10) === digits) : null) ||
      null
    );
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setDraft((d) => ({ ...d, [name]: value }));
  };

  // --- VoIP.ms line list editors ---
  const addLine = () => setDraft((d) => ({
    ...d, subaccounts: [...(d.subaccounts || []), { subaccount: '', did: '', user: '' }],
  }));
  const removeLine = (idx) => setDraft((d) => ({
    ...d, subaccounts: (d.subaccounts || []).filter((_, i) => i !== idx),
  }));
  const updateLine = (idx, field, value) => setDraft((d) => ({
    ...d,
    subaccounts: (d.subaccounts || []).map((l, i) => (i === idx ? { ...l, [field]: value } : l)),
  }));

  const onProviderChange = (e) => {
    const next = e.target.value;
    setDraft((d) => ({ ...d, provider: next }));
    setMessage('');
  };

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');
    try {
      // Only send fields the operator actually filled in (empty = "leave
      // alone", matching the masked-on-load UX). `provider` always goes
      // because the dropdown is authoritative for the active provider.
      const payload = { provider };
      for (const [k, v] of Object.entries(draft)) {
        if (k === 'provider' || k === 'subaccounts') continue;
        if (v && String(v).trim() !== '') payload[k] = v;
      }
      // VoIP.ms: always send the line list (so removals stick), keeping
      // only rows that name a sub-account. Designate the first line's DID
      // as the org's SMS sender so outbound SMS tracks the primary number.
      if (provider === 'voipms') {
        const lines = (draft.subaccounts || [])
          .map((l) => ({
            subaccount: String(l.subaccount || '').trim(),
            did: String(l.did || '').trim(),
            user: l.user || null,
            label: l.label || '',
          }))
          .filter((l) => l.subaccount);
        payload.subaccounts = lines;
        const primaryDid = lines.find((l) => l.did)?.did;
        if (primaryDid) payload.smsDid = primaryDid;
      }
      const { data } = await api.put('/voip/settings', payload);
      setSettings(data);
      setDraft(draftFromData(data));

      // Verify after save: a DB-layer success that fails on auth would
      // otherwise sit silently until the next poll fires an error.
      if (data.isConfigured) {
        try {
          const tc = await api.post('/voip/test-connection');
          if (tc.data?.ok) {
            setMessage('Settings saved and connection verified ✅');
            setMessageType('success');
          } else {
            setMessage(`Settings saved but connection failed: ${tc.data?.error || 'unknown error'}`);
            setMessageType('error');
          }
        } catch (tcErr) {
          setMessage(`Settings saved but connection test failed: ${tcErr.response?.data?.error || tcErr.message}`);
          setMessageType('error');
        }
        await load();
      } else {
        setMessage('Settings saved (connection will be tested once all required fields are filled).');
        setMessageType('success');
      }
    } catch (err) {
      setMessage(err.response?.data?.error || 'Failed to save settings');
      setMessageType('error');
    } finally {
      setSaving(false);
    }
  };

  const onTest = async () => {
    setTesting(true);
    setMessage('');
    try {
      const { data } = await api.post('/voip/test-connection');
      if (data.ok) {
        setMessage(provider === 'voipms'
          ? 'Connection OK — Gain Rhino VoIP credentials accepted.'
          : 'Connection OK — token obtained from NetSapiens.');
        setMessageType('success');
      } else {
        setMessage(data.error || 'Connection failed');
        setMessageType('error');
      }
      await load();
    } catch (err) {
      setMessage(err.response?.data?.error || 'Connection failed');
      setMessageType('error');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading VoIP settings…</p>
        </div>
      </div>
    );
  }

  const fmtTs = (d) => d ? new Date(d).toLocaleString() : '—';
  const providerLabel = PROVIDERS.find((p) => p.value === provider)?.label || provider;
  const extensionTermSingular = provider === 'voipms' ? 'sub-account' : 'extension';
  const extensionPlaceholder = provider === 'voipms' ? 'e.g. 545782_gain' : 'e.g. 101';

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow-lg rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Phone size={24} className="text-teal-600" />
                VoIP Settings
              </h1>
              <p className="mt-1 text-sm text-gray-600">
                Connect your PBX so inbound and outbound calls land in lead timelines automatically. NetSapiens and Gain Rhino VoIP supported — pick yours from the dropdown below.
              </p>
            </div>
            {(() => {
              const s = settings.status || (settings.isConfigured ? 'configured' : 'not_configured');
              const styles = {
                connected:      { cls: 'bg-green-100 text-green-700', label: 'Connected' },
                configured:     { cls: 'bg-amber-100 text-amber-700', label: 'Configured — test connection to verify' },
                not_configured: { cls: 'bg-gray-100 text-gray-600',   label: 'Not configured' },
              }[s];
              return (
                <div className={`px-3 py-1 rounded-full text-xs font-medium ${styles.cls}`}>
                  {styles.label}
                </div>
              );
            })()}
          </div>

          {/* Status strip */}
          {settings.isConfigured && (
            <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
              <div>
                <span className="block text-gray-500">Last CDR poll</span>
                <span className="font-medium text-gray-900">{fmtTs(settings.lastPolledCdrAt)}</span>
              </div>
              {provider === 'netsapiens' ? (
                <div>
                  <span className="block text-gray-500">Cached token expires</span>
                  <span className="font-medium text-gray-900">{settings.hasToken ? fmtTs(settings.tokenExpiresAt) : '—'}</span>
                </div>
              ) : (
                <div>
                  <span className="block text-gray-500">Last verified</span>
                  <span className="font-medium text-gray-900">{fmtTs(settings.lastTestedAt)}</span>
                </div>
              )}
              <div>
                <span className="block text-gray-500">Last error</span>
                <span className={`font-medium ${settings.lastErrorMessage ? 'text-red-600' : 'text-gray-900'}`}>
                  {settings.lastErrorMessage ? `${settings.lastErrorMessage} (${fmtTs(settings.lastErrorAt)})` : 'None'}
                </span>
              </div>
            </div>
          )}

          {message && (
            <div className={`mx-6 mt-4 p-4 rounded-md flex items-start gap-3 border-2 ${
              messageType === 'success' ? 'bg-green-50 border-green-300 text-green-900'
                : 'bg-red-50 border-red-300 text-red-900'
            }`}>
              {messageType === 'success' ? <CheckCircle2 size={22} className="flex-shrink-0 mt-0.5" /> : <AlertCircle size={22} className="flex-shrink-0 mt-0.5" />}
              <span className="font-medium">{message}</span>
            </div>
          )}

          {/* Your extension — quick self-assign. NetSapiens only: for
              Gain Rhino VoIP the admin-managed line list below is the
              attribution source, so a second per-user field here would
              just duplicate it (the poller keeps User.voip.extension as
              a back-compat fallback, but no new org should set it). */}
          {currentUser && provider !== 'voipms' && (
            <div className="mx-6 mt-4 p-4 bg-teal-50 border border-teal-200 rounded-md">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-teal-900">Your VoIP {extensionTermSingular}</h3>
                  <p className="text-xs text-teal-700 mt-0.5">
                    The {extensionTermSingular} tied to your CRM account. The CDR poller uses this to attribute calls to you.
                    {currentUser.voip?.extension ? ` Currently set: ${currentUser.voip.extension}` : ' Not set yet.'}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <input
                    type="text"
                    value={myExt}
                    onChange={(e) => setMyExt(e.target.value)}
                    placeholder={extensionPlaceholder}
                    className="px-3 py-1.5 border border-teal-300 rounded-md text-sm font-mono w-40 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  <button
                    type="button"
                    onClick={saveMyExt}
                    disabled={savingExt || myExt === (currentUser.voip?.extension || '')}
                    className="px-3 py-1.5 bg-teal-600 text-white rounded-md text-sm hover:bg-teal-700 disabled:opacity-50"
                  >
                    {savingExt ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={onSave} className="px-6 py-6">
            {/* Provider selector — drives which credential block renders below. */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">VoIP Provider</label>
              <select
                name="provider"
                value={provider}
                onChange={onProviderChange}
                className="w-full md:w-1/2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
              >
                {PROVIDERS.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Switching providers preserves both sets of credentials — you can move back without re-entering setup.
              </p>
            </div>

            {/* NetSapiens form */}
            {provider === 'netsapiens' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">PBX Base URL</label>
                  <input type="text" name="baseUrl" value={draft.baseUrl} onChange={onChange}
                    placeholder={settings.baseUrl || 'https://your-pbx.dashmanager.com/ns-api'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500" />
                  <p className="text-xs text-gray-500 mt-1">Must include the <code>/ns-api</code> suffix. No trailing slash.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Client ID</label>
                  <input type="text" name="clientId" value={draft.clientId} onChange={onChange}
                    placeholder={settings.clientId ? `Saved (${settings.clientId}) — leave blank to keep` : 'API Application Client ID'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Client Secret</label>
                  <input type="password" name="clientSecret" value={draft.clientSecret} onChange={onChange}
                    placeholder={settings.clientSecret ? 'Saved — leave blank to keep' : '••••••••'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
                  <input type="text" name="username" value={draft.username} onChange={onChange}
                    placeholder={settings.username || 'api@territory'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500" />
                  <p className="text-xs text-gray-500 mt-1">Reseller / Office Manager NS user with API access.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                  <input type="password" name="password" value={draft.password} onChange={onChange}
                    placeholder={settings.password ? 'Saved — leave blank to keep' : '••••••••'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500" />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Domain</label>
                  <input type="text" name="domain" value={draft.domain} onChange={onChange}
                    placeholder={settings.domain || 'tenant.territory.service'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500" />
                  <p className="text-xs text-gray-500 mt-1">The NetSapiens "domain" your subscribers live under. CDRs are scoped to this.</p>
                </div>
              </div>
            )}

            {/* VoIP.ms (Gain Rhino VoIP) form — reseller model. Master API
                credentials are managed by Gain Rhino server-side; the client
                only supplies the two per-account isolation keys. */}
            {provider === 'voipms' && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Sub-accounts &amp; numbers</label>
                  <div className="flex items-center gap-2">
                    {callCounts?.total && (
                      <span
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold text-teal-800 bg-teal-50 border border-teal-200 rounded-full"
                        title={`Since midnight — ${callCounts.total.outbound} outbound, ${callCounts.total.inbound} inbound`}
                      >
                        <PhoneOutgoing size={12} />
                        {callCounts.total.outbound} calls made today
                      </span>
                    )}
                    {bcUsage?.bridgeConfigured && (() => {
                      const used = Math.round((bcUsage.usedSeconds || 0) / 60);
                      const limit = bcUsage.limitMinutes || 0;
                      const full = limit > 0 && used >= limit;
                      return (
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full border ${
                            full ? 'text-red-800 bg-red-50 border-red-200' : 'text-indigo-800 bg-indigo-50 border-indigo-200'
                          }`}
                          title="Minutes called from the browser this month (click-to-call). Contact Gain Rhino to raise the limit."
                        >
                          <PhoneCall size={12} />
                          Browser minutes {used} / {limit}
                        </span>
                      );
                    })()}
                    <button type="button" onClick={loadCallCounts} disabled={countsLoading}
                      title="Refresh today's call counts"
                      className="p-1.5 text-gray-400 hover:text-teal-700 hover:bg-teal-50 rounded-md disabled:opacity-50">
                      <RefreshCw size={14} className={countsLoading ? 'animate-spin' : ''} />
                    </button>
                    {canAssignLines && (
                      <button type="button" onClick={addLine}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-teal-50 text-teal-700 border border-teal-200 rounded-md hover:bg-teal-100">
                        <Plus size={15} /> Add line
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-xs text-gray-500 mb-3">
                  One row per sub-account Gain Rhino assigned you. Each can have its own phone number (DID) and is attributed to a rep — calls from that sub-account land on the lead's timeline under that rep. The first row's number is used as the SMS sender. Sharing one number across sub-accounts is supported; inbound calls to a shared number are reported as "shared" because the carrier can't attribute them to a single sub-account.
                </p>
                {!canAssignLines && (
                  <p className="text-xs text-gray-600 mb-3 px-3 py-2 bg-gray-50 border border-gray-200 rounded-md">
                    Sub-accounts and phone numbers are assigned by Gain Rhino. To add, change or remove a line, contact Gain Rhino support. You can choose which rep each line belongs to.
                  </p>
                )}

                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <Globe size={14} className="text-gray-400 flex-shrink-0" />
                  <label className="text-xs font-medium text-gray-600">Organization timezone</label>
                  <select
                    value={orgTz}
                    onChange={onTimezoneChange}
                    disabled={savingTz || !isOrgAdmin}
                    title={isOrgAdmin ? '' : 'Only administrators can change the organization timezone'}
                    className="text-xs border border-gray-300 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 max-w-full disabled:opacity-60"
                  >
                    {TZ_OPTIONS.map((z) => (
                      <option key={z} value={z}>{z}</option>
                    ))}
                  </select>
                  <span className="text-xs text-gray-400">
                    "Today" counters reset at midnight in this timezone.
                  </span>
                </div>

                {(draft.subaccounts || []).length === 0 && (
                  <div className="text-sm text-gray-500 border border-dashed border-gray-300 rounded-md px-4 py-6 text-center">
                    {canAssignLines
                      ? <>No sub-accounts yet. Click <strong>Add line</strong> to add the first one.</>
                      : 'No lines assigned yet. Contact Gain Rhino support to get a phone number.'}
                  </div>
                )}

                <div className="space-y-3">
                  {(draft.subaccounts || []).map((line, idx) => (
                    <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start border border-gray-200 rounded-md p-3 bg-gray-50/60">
                      {line.label && (
                        <div className="md:col-span-12 text-xs font-semibold text-gray-700">{line.label}</div>
                      )}
                      <div className="md:col-span-4">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Sub-account</label>
                        <input type="text" value={line.subaccount}
                          onChange={(e) => updateLine(idx, 'subaccount', e.target.value)}
                          readOnly={!canAssignLines}
                          placeholder="e.g. 545782_luxe2"
                          className={`w-full px-3 py-2 text-sm font-mono border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${canAssignLines ? '' : 'bg-gray-100 text-gray-700 cursor-default'}`} />
                      </div>
                      <div className="md:col-span-3">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Phone (DID)</label>
                        <input type="text" value={line.did}
                          onChange={(e) => updateLine(idx, 'did', e.target.value)}
                          readOnly={!canAssignLines}
                          placeholder="e.g. 4244310030"
                          className={`w-full px-3 py-2 text-sm font-mono border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${canAssignLines ? '' : 'bg-gray-100 text-gray-700 cursor-default'}`} />
                      </div>
                      <div className="md:col-span-4">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Rep (who calls)</label>
                        <select value={line.user || ''}
                          onChange={(e) => updateLine(idx, 'user', e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-teal-500">
                          <option value="">— Unassigned (logs to admin) —</option>
                          {orgUsers.map((u) => (
                            <option key={u._id} value={u._id}>{u.name || u.email}</option>
                          ))}
                        </select>
                      </div>
                      {canAssignLines && (
                        <div className="md:col-span-1 flex md:justify-center md:pt-6">
                          <button type="button" onClick={() => removeLine(idx)}
                            title="Remove line"
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      )}
                      {bcUsage?.bridgeConfigured && line.subaccount && (() => {
                        const verified = lineVerified(line);
                        const needsSave = lineNeedsSave(line);
                        const active = verify && verify.subaccount === line.subaccount;
                        return (
                          <div className="md:col-span-12 pt-2 border-t border-gray-200 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs">
                            <span className="inline-flex items-center gap-1.5 text-gray-600 font-medium">
                              <PhoneCall size={13} /> Browser calling
                            </span>
                            {verified ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200 font-semibold"
                                title="This number is verified as a caller ID. The assigned rep can call leads from the browser.">
                                <ShieldCheck size={12} /> Verified
                              </span>
                            ) : active && verify.status === 'waiting' ? (
                              <span className="inline-flex flex-wrap items-center gap-2 text-gray-700">
                                <Loader2 size={13} className="animate-spin text-teal-600" />
                                Answer the call on <strong>{verify.phoneNumber}</strong> and enter this code:
                                <span className="font-mono text-base font-bold tracking-[0.3em] px-2 py-0.5 bg-white border border-teal-300 rounded-md text-teal-800">{verify.code}</span>
                                <button type="button" onClick={() => setVerify(null)} className="text-gray-500 hover:text-gray-800 underline">cancel</button>
                              </span>
                            ) : active && verify.status === 'starting' ? (
                              <span className="inline-flex items-center gap-1.5 text-gray-600"><Loader2 size={13} className="animate-spin" /> Starting verification…</span>
                            ) : (
                              <>
                                {!line.did ? (
                                  <span className="text-gray-500">Add a phone number (DID) to enable browser calling.</span>
                                ) : needsSave ? (
                                  <span className="text-gray-500">Save settings, then verify this number.</span>
                                ) : (
                                  <span className="text-gray-500">Not verified — the rep on this line can't call from the browser yet.</span>
                                )}
                                <button type="button" onClick={() => startVerify(line)} disabled={!line.did || needsSave}
                                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-teal-600 text-white font-semibold hover:bg-teal-700 disabled:opacity-40 disabled:cursor-not-allowed"
                                  title="Twilio will call this number and read a code; enter it on your Gain Rhino VoIP phone.">
                                  <ShieldCheck size={12} /> Verify
                                </button>
                                {active && verify.status === 'error' && (
                                  <span className="text-red-600">{verify.error}</span>
                                )}
                              </>
                            )}
                          </div>
                        );
                      })()}
                      {(() => {
                        const c = countsForLine(line);
                        if (!c) return null;
                        const shared = sharedForLine(line);
                        const inboundCount = shared ? shared.inbound : c.inbound;
                        const abandonRate = shared ? shared.abandonRate : c.abandonRate;
                        const talkOk = c.avgTalkSec != null && c.avgTalkSec >= 60;
                        const abandonOk = abandonRate != null && abandonRate < 0.03;
                        return (
                          <div className="md:col-span-12 pt-2 border-t border-gray-200 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs">
                            <span className="inline-flex items-center gap-1.5 font-semibold text-teal-700">
                              <PhoneOutgoing size={13} /> {c.outbound} call{c.outbound === 1 ? '' : 's'} made today
                            </span>
                            <span className="inline-flex items-center gap-1.5 text-gray-500">
                              <PhoneIncoming size={13} /> {inboundCount} received
                              {shared && (
                                <span
                                  className="px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-[10px] font-semibold uppercase tracking-wide"
                                  title={`This number is shared by: ${shared.subaccounts.join(', ')}. Inbound calls can't be attributed to a single sub-account.`}
                                >
                                  shared
                                </span>
                              )}
                            </span>
                            <span className="inline-flex items-center gap-1 text-gray-600" title="Answered outbound ÷ outbound attempts (today)">
                              Answer rate <strong className="text-gray-800">{pct(c.answerRate)}</strong>
                            </span>
                            <span
                              className={`inline-flex items-center gap-1 ${c.avgTalkSec == null ? 'text-gray-600' : talkOk ? 'text-green-700' : 'text-amber-700'}`}
                              title="Average duration of answered outbound calls (today). Target ≥ 60s"
                            >
                              Avg talk <strong>{c.avgTalkSec == null ? '—' : `${c.avgTalkSec}s`}</strong>
                            </span>
                            <span
                              className={`inline-flex items-center gap-1 ${abandonRate == null ? 'text-gray-600' : abandonOk ? 'text-green-700' : 'text-red-700'}`}
                              title="Unanswered inbound ÷ inbound (today). Target < 3%"
                            >
                              Abandon <strong>{pct(abandonRate)}</strong>
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {provider === 'netsapiens' && callCounts?.total?.calls != null && (
              <p className="mt-4 text-xs text-gray-500">
                <PhoneOutgoing size={12} className="inline mr-1" />
                {callCounts.total.calls} calls today across all lines (since midnight).
              </p>
            )}

            <div className="mt-8 rounded-md border border-gray-200 p-4">
              <div className="flex items-center gap-2 mb-1">
                <Languages size={16} className="text-gray-500" />
                <h3 className="text-sm font-semibold text-gray-900">Call languages</h3>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                The languages your callers speak. Call recordings are transcribed in these languages only, so an accent is never mistaken for another language. With one language every call is transcribed in it; with several, the language is detected among them. Summaries are always written in English.
              </p>
              <div className="flex flex-wrap gap-2">
                {CALL_LANGUAGE_OPTIONS.map(([code, label]) => {
                  const on = (settings?.callLanguages?.length ? settings.callLanguages : ['en']).includes(code);
                  return (
                    <button
                      key={code}
                      type="button"
                      onClick={() => toggleCallLanguage(code)}
                      disabled={savingLangs}
                      aria-pressed={on}
                      className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors disabled:opacity-60 ${on ? 'bg-teal-600 border-teal-600 text-white' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <button type="submit" disabled={saving}
                className="px-6 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50">
                {saving ? 'Saving…' : 'Save Settings'}
              </button>
              <button type="button" onClick={onTest} disabled={testing || !settings.isConfigured}
                className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center gap-2">
                <RefreshCw size={16} className={testing ? 'animate-spin' : ''} />
                {testing ? 'Testing…' : 'Test Connection'}
              </button>
            </div>
          </form>

          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-3">How it works ({providerLabel})</h3>
            <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
              <li>Every 30 seconds, the CRM pulls new Call Detail Records (CDRs) from your {providerLabel} account.</li>
              <li>Calls are matched to leads by phone number (last 10 digits), then attached to the lead's timeline.</li>
              <li>When a recording is available, an inline audio player appears on the lead's call note.</li>
              {provider === 'voipms' ? (
                <li>{canAssignLines ? 'Add one line per sub-account above and pick' : 'Pick'} the rep who uses each line — every call from that sub-account is attributed to that rep. Unassigned lines log to an org admin.</li>
              ) : (
                <li>To assign extensions to reps for call attribution, open <strong>Team Members</strong> and set each user's extension.</li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoipSettings;
